import { format, isToday, parse } from 'date-fns';

import { reservationTimeWindow } from '@/stores/tableReservationSettings';
import { getEffectiveEndMinutes } from '@/extensions/tableReservation/store/utils';

export const TIMELINE_SLOT_MINUTES = reservationTimeWindow.timelineSlotMinutes;

const pad = (n) => (n < 10 ? '0' + n : '' + n);

const buildTimeSlots = () => {
    const start = reservationTimeWindow.start;
    const end = reservationTimeWindow.end;
    const slots = [];
    const startHour = parseInt(start.split(':')[0], 10);
    const startMin = parseInt(start.split(':')[1], 10);
    const endHour = parseInt(end.split(':')[0], 10);
    const endMin = parseInt(end.split(':')[1], 10);

    let h = startHour;
    let m = startMin;
    while (h < endHour || (h === endHour && m <= endMin)) {
        slots.push(pad(h) + ':' + pad(m));
        m += TIMELINE_SLOT_MINUTES;
        if (m >= 60) {
            m = 0;
            h++;
        }
    }
    return slots;
};

/**
 * Shared logic between the multi-table day timeline (Timeline/index.vue) and the
 * single-table side panel (FloorPlan/components/TableTimelinePanel.vue).
 *
 * Provides:
 *  - currentTime + per-minute refresh interval
 *  - timeSlots, isFullHour, isCurrentSlot, time arithmetic
 *  - reservation effective-end calculation (getReservationDurationMinutes)
 *  - reservation drag start/end + showActionError
 */
export default {
    data: function () {
        return {
            currentTime: format(new Date(), 'HH:mm'),
            draggingResId: null,
            _timelineCurrentTimeInterval: null
        };
    },
    computed: {
        timeSlots: function () {
            return buildTimeSlots();
        }
    },
    mounted: function () {
        this._timelineCurrentTimeInterval = setInterval(() => {
            this.currentTime = format(new Date(), 'HH:mm');
        }, 60000);
    },
    beforeDestroy: function () {
        if (this._timelineCurrentTimeInterval) {
            clearInterval(this._timelineCurrentTimeInterval);
            this._timelineCurrentTimeInterval = null;
        }
    },
    methods: {
        padTime: function (n) {
            return pad(n);
        },
        isFullHour: function (slot) {
            return slot.endsWith(':00');
        },
        timeToMinutes: function (time) {
            const parts = time.split(':');
            return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
        },
        slotIndex: function (time) {
            const startMins = this.timeToMinutes(this.timeSlots[0]);
            return (this.timeToMinutes(time) - startMins) / TIMELINE_SLOT_MINUTES;
        },
        isDateToday: function (dateStr) {
            return isToday(parse(dateStr, 'yyyy-MM-dd', new Date()));
        },
        isCurrentSlot: function (slot, dateStr) {
            if (!this.isDateToday(dateStr)) return false;
            const nowMins = this.timeToMinutes(this.currentTime);
            const slotMins = this.timeToMinutes(slot);
            return nowMins >= slotMins && nowMins < slotMins + TIMELINE_SLOT_MINUTES;
        },
        getReservationDurationMinutes: function (res, sameContextReservations, dateStr) {
            const startMins = this.timeToMinutes(res.time);
            const endMins = this.isDateToday(dateStr)
                ? getEffectiveEndMinutes(res, sameContextReservations, this.timeToMinutes(this.currentTime))
                : startMins + (res.duration || 90);
            return { startMins, endMins, durationMins: endMins - startMins };
        },
        onResDragStart: function (res, event) {
            if (!this.canUpdateReservation) return;
            this.draggingResId = res.id;
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', res.id);
        },
        onResDragEnd: function () {
            this.draggingResId = null;
        },
        showActionError: function (error) {
            this.$message({
                message: error.message || this.$t('Something went wrong'),
                type: 'error',
                duration: 5000
            });
        }
    }
};
